/**
 * DeliveryGlyph — the one rung of the delivery ladder a user bubble shows
 * (2026-09-08, chat-thread experience PR-2): ○ sending · ⧗ offline · ✓ sent ·
 * ✓✓ delivered · ✓✓ seen (accent) · ! not sent. Sits in the nowrap
 * MessageTime row next to the timestamp; the full word is the tooltip and the
 * accessible label. Pure presentational — the status is derived by the host
 * from the delivery ledger, never stored on the message.
 */
import React from 'react';
import { DELIVERY_STATUS_COPY, type DeliveryStatus } from '../../services/messageDelivery/deliveryStatus';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';

const COLOR: Record<DeliveryStatus, string> = {
  pending: 'rgba(127,127,127,0.55)',
  'queued-offline': 'rgba(127,127,127,0.75)',
  sent: 'rgba(127,127,127,0.85)',
  delivered: 'rgba(127,127,127,0.85)',
  read: '#8a6d2f',
  failed: '#b42318',
};

export interface DeliveryGlyphProps {
  status: DeliveryStatus;
  /** Optional detail for the tooltip (a failure reason, a seen-by list). */
  detail?: string;
}

export function DeliveryGlyph({ status, detail }: DeliveryGlyphProps): React.ReactElement {
  const copy = DELIVERY_STATUS_COPY[status];
  const label = getUserUiLanguage() === 'es' ? copy.es : copy.label;
  const title = detail ? `${label} — ${detail}` : label;
  return (
    <span
      role="img"
      aria-label={title}
      title={title}
      data-delivery-status={status}
      style={{
        marginLeft: 6,
        fontSize: '0.7rem',
        letterSpacing: status === 'delivered' || status === 'read' ? '-0.15em' : undefined,
        color: COLOR[status],
        fontWeight: status === 'failed' ? 700 : 500,
        whiteSpace: 'nowrap',
      }}
    >
      {copy.glyph}
    </span>
  );
}

export default DeliveryGlyph;
